import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, ArrowUpRight, Check } from "lucide-react";
import { useSEO } from "@/hooks/useSEO";
import { Reveal, MaskedLine } from "@/components/Reveal";
import SectionHeader from "@/components/SectionHeader";
import SkillMarquee from "@/components/Marquee";
import FAQ from "@/components/FAQ";
import DeliveryDiagram from "@/components/DeliveryDiagram";
import CTASection from "@/components/CTASection";
import { PROBLEMS, SERVICES, BENEFITS, STEPS, INDUSTRIES, PRICING, FAQS, CASE_STUDIES, ROLES } from "@/data/content";

const HERO_IMG = "/hero-connect.png";

function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 90]);
  const cardY = useTransform(scrollYProgress, [0, 1], [0, -50]);

  return (
    <section ref={ref} data-testid="hero" className="relative overflow-hidden pb-14 pt-32 sm:pt-40 lg:pb-20">
      <div className="spotlight absolute inset-0" aria-hidden="true" />
      <div className="bg-grid fade-b absolute inset-0" aria-hidden="true" />
      <div className="relative mx-auto grid max-w-7xl items-center gap-14 px-4 sm:px-6 lg:grid-cols-12 lg:gap-10 lg:px-8">
        <div className="lg:col-span-7">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="inline-flex items-center gap-2 rounded-full border border-brand/25 bg-brand-soft px-4 py-1.5"
            data-testid="hero-eyebrow"
          >
            <span className="h-1.5 w-1.5 rounded-full bg-brand" aria-hidden="true" />
            <span className="font-mono text-[11px] font-semibold uppercase tracking-[0.18em] text-brand">
              Managed global talent for growing Australian businesses
            </span>
          </motion.div>

          <h1 className="mt-7 font-display text-4xl font-extrabold leading-[1.08] tracking-tight text-white sm:text-5xl lg:text-6xl">
            <MaskedLine delay={0.2}>Build a better team</MaskedLine>
            <MaskedLine delay={0.32}>without building</MaskedLine>
            <MaskedLine delay={0.44}>
              a <span className="text-brand">bigger overhead.</span>
            </MaskedLine>
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.7, ease: [0.16, 1, 0.3, 1] }}
            className="mt-6 max-w-xl text-base leading-relaxed text-mist sm:text-lg"
            data-testid="hero-copy"
          >
            Synergy Outsourcing helps you build dedicated teams across business operations, customer support, sales,
            finance and professional services, with structured management and accountable delivery.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.85, ease: [0.16, 1, 0.3, 1] }}
            className="mt-8 flex flex-col gap-3 sm:flex-row"
          >
            <Link
              to="/get-your-team"
              data-testid="hero-build-your-team"
              className="group flex items-center justify-center gap-2 rounded-full bg-brand px-7 py-3.5 text-sm font-semibold text-ink transition-colors hover:bg-brand-bright"
            >
              Build Your Team
              <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
            </Link>
            <Link
              to="/solutions"
              data-testid="hero-explore-solutions"
              className="flex items-center justify-center gap-2 rounded-full border border-white/15 px-7 py-3.5 text-sm font-semibold text-slate-200 transition-colors hover:border-brand/50 hover:text-white"
            >
              Explore Solutions
            </Link>
          </motion.div>

          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 1.05 }}
            className="mt-8 font-mono text-[11px] uppercase tracking-[0.18em] text-slate-500"
            data-testid="hero-credibility"
          >
            Trusted by businesses across Australia, the US and Canada
          </motion.p>
        </div>

        <div className="relative hidden lg:col-span-5 lg:block">
          <motion.div
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1, delay: 0.5, ease: [0.16, 1, 0.3, 1] }}
            className="relative"
          >
            <motion.div style={{ y: imgY }} className="overflow-hidden rounded-2xl border border-white/10">
              <img
                src={HERO_IMG}
                alt="Clients in the USA, Canada and Australia connected through Synergy Outsourcing to global talent professionals"
                className="aspect-[4/5] w-full object-cover"
                loading="eager"
                data-testid="hero-image"
              />
            </motion.div>
            <motion.div
              style={{ y: cardY }}
              className="absolute -bottom-6 -left-4 rounded-2xl border border-white/10 bg-ink-2/95 p-5 shadow-2xl backdrop-blur-md sm:-left-8"
              data-testid="hero-rate-card"
            >
              <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">Starting rate</p>
              <p className="mt-1 font-display text-3xl font-extrabold text-white">
                $8<span className="text-base font-semibold text-mist">/hr</span>
              </p>
              <p className="mt-1 text-xs text-mist">for basic roles</p>
            </motion.div>
            <div className="absolute -right-3 top-6 flex flex-col gap-2 sm:-right-6" data-testid="hero-badges">
              {["Dedicated", "Managed", "Accountable"].map((b, i) => (
                <motion.span
                  key={b}
                  initial={{ opacity: 0, x: 16 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6, delay: 0.9 + i * 0.15 }}
                  className="rounded-full border border-white/10 bg-ink-2/85 px-3.5 py-1.5 font-mono text-[10px] font-semibold uppercase tracking-[0.15em] text-brand backdrop-blur-md"
                >
                  {b}
                </motion.span>
              ))}
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}

function Problem() {
  return (
    <section data-testid="problem-section" className="py-16 sm:py-20">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <SectionHeader
          eyebrow="The problem"
          title="Your business is growing. Your overhead doesn't need to grow with it."
          copy="Hiring locally for every new role can be expensive, slow and difficult to scale. Synergy Outsourcing gives growing businesses access to dedicated professionals without requiring you to build and manage an entirely new internal team."
        />
        <div className="mt-14 grid gap-6 md:grid-cols-3">
          {PROBLEMS.map((p, i) => (
            <Reveal key={p.num} delay={i * 0.12}>
              <div
                data-testid={`problem-card-${i}`}
                className="card-hover h-full rounded-2xl border border-white/10 bg-ink-2 p-8"
              >
                <span className="font-mono text-sm font-semibold text-brand">{p.num}</span>
                <h3 className="mt-4 font-display text-xl font-semibold text-white">{p.title}</h3>
                <p className="mt-3 text-sm leading-relaxed text-mist">{p.copy}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

function Services() {
  return (
    <section data-testid="services-section" className="bg-ink-2/40 py-16 sm:py-20">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <SectionHeader
          eyebrow="What Synergy Outsourcing does"
          title="The people you need. The management you don't have time for."
          copy="You define the role. We source the talent, establish the team and manage the delivery. You get professionals who become an extension of your business."
        />
        <div className="mt-14 grid grid-cols-1 gap-6 md:grid-cols-3">
          {SERVICES.map((s, i) => (
            <Reveal key={s.name} delay={i * 0.1} className={s.featured ? "md:col-span-2" : ""}>
              <div
                data-testid={`service-card-${s.name.toLowerCase().replace(/[\s&]+/g, "-")}`}
                className={`card-hover h-full rounded-2xl border p-8 ${
                  s.featured
                    ? "tracing-border border-brand/30 bg-gradient-to-br from-ink-2 to-brand-soft/60"
                    : "border-white/10 bg-ink-2"
                }`}
              >
                <div className={s.featured ? "grid gap-8 lg:grid-cols-2" : ""}>
                  <div>
                    <div className="flex items-center gap-3">
                      <span className={`flex h-11 w-11 items-center justify-center rounded-xl ${s.featured ? "bg-brand text-ink" : "bg-brand-soft text-brand"}`}>
                        <s.icon className="h-5 w-5" aria-hidden="true" />
                      </span>
                      {s.featured && (
                        <span className="rounded-full border border-brand/40 bg-brand-soft px-3 py-1 font-mono text-[10px] font-semibold uppercase tracking-[0.15em] text-brand">
                          Strategic growth area
                        </span>
                      )}
                    </div>
                    <h3 className="mt-5 font-display text-xl font-semibold text-white">{s.name}</h3>
                    <p className="mt-3 text-sm leading-relaxed text-mist">{s.copy}</p>
                    <Link
                      to={s.href}
                      data-testid={`service-cta-${s.name.toLowerCase().replace(/[\s&]+/g, "-")}`}
                      className="group mt-5 inline-flex items-center gap-1.5 text-sm font-semibold text-brand transition-colors hover:text-brand-deep"
                    >
                      {s.cta}
                      <ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                    </Link>
                  </div>
                  <ul className={`grid gap-2 ${s.featured ? "content-center sm:grid-cols-2" : "mt-6"}`}>
                    {s.examples.map((ex) => (
                      <li key={ex} className="flex items-start gap-2 text-sm text-slate-300">
                        <Check className="mt-0.5 h-4 w-4 shrink-0 text-brand" aria-hidden="true" />
                        {ex}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

function ProServicesFeature() {
  return (
    <section data-testid="pro-services-feature" className="py-16 sm:py-20">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="grid items-start gap-12 lg:grid-cols-12">
          <div className="lg:col-span-4">
            <SectionHeader
              eyebrow="Professional services"
              title="Don't just outsource tasks. Add professional capability."
              copy="Your next Business Analyst, Project Manager, Data Analyst or Finance professional doesn't necessarily need to sit in your office. Synergy Outsourcing provides dedicated professionals who can become an extension of your internal team."
            />
            <Reveal delay={0.2}>
              <Link
                to="/get-your-team"
                data-testid="pro-services-feature-cta"
                className="group mt-8 inline-flex items-center gap-2 rounded-full bg-brand px-6 py-3 text-sm font-semibold text-ink transition-colors hover:bg-brand-bright"
              >
                Build a Professional Services Team
                <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
              </Link>
            </Reveal>
          </div>
          <div className="grid gap-4 sm:grid-cols-2 lg:col-span-8">
            {ROLES.map((r, i) => (
              <Reveal key={r.name} delay={i * 0.06}>
                <Link
                  to="/professional-services"
                  data-testid={`role-card-${r.name.toLowerCase().replace(/[\s&]+/g, "-")}`}
                  className="card-hover flex h-full items-center gap-4 rounded-2xl border border-white/10 bg-ink-2 p-5"
                >
                  <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-brand-soft text-brand">
                    <r.icon className="h-5 w-5" aria-hidden="true" />
                  </span>
                  <div>
                    <h3 className="font-display text-base font-semibold text-white">{r.name}</h3>
                    <p className="mt-0.5 text-xs text-mist">{r.tagline}</p>
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

function WhySynergy() {
  return (
    <section data-testid="why-section" className="bg-ink-2/40 py-16 sm:py-20">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <SectionHeader eyebrow="Why Synergy Outsourcing" title="A different approach to outsourcing." align="center" />
        <div className="mx-auto mt-14 grid max-w-5xl gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {BENEFITS.map((b, i) => (
            <Reveal key={b.title} delay={i * 0.08} className={i === 4 ? "sm:col-span-2 lg:col-span-1" : ""}>
              <div data-testid={`benefit-card-${i}`} className="card-hover h-full rounded-2xl border border-white/10 bg-ink p-7">
                <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-brand-soft text-brand">
                  <b.icon className="h-5 w-5" aria-hidden="true" />
                </span>
                <h3 className="mt-4 font-display text-lg font-semibold text-white">{b.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-mist">{b.copy}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

function HowItWorks() {
  return (
    <section data-testid="how-it-works-section" className="py-16 sm:py-20">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <SectionHeader eyebrow="How it works" title="From role to productive team." />
        <div className="relative mt-14 grid gap-10 md:grid-cols-4 md:gap-6">
          <div className="absolute left-0 right-0 top-6 hidden h-px bg-gradient-to-r from-transparent via-white/15 to-transparent md:block" aria-hidden="true" />
          {STEPS.map((s, i) => (
            <Reveal key={s.num} delay={i * 0.12}>
              <div data-testid={`step-${s.num}`} className="relative">
                <span className="relative z-10 flex h-12 w-12 items-center justify-center rounded-full border border-brand/40 bg-ink font-mono text-sm font-bold text-brand">
                  {s.num}
                </span>
                <h3 className="mt-5 font-display text-lg font-semibold text-white">{s.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-mist">{s.copy}</p>
              </div>
            </Reveal>
          ))}
        </div>
        <Reveal delay={0.3}>
          <Link
            to="/get-your-team"
            data-testid="how-it-works-cta"
            className="group mt-12 inline-flex items-center gap-2 rounded-full bg-brand px-6 py-3 text-sm font-semibold text-ink transition-colors hover:bg-brand-bright"
          >
            Build Your Team
            <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
          </Link>
        </Reveal>
      </div>
    </section>
  );
}

function GlobalDelivery() {
  return (
    <section data-testid="global-delivery-section" className="bg-ink-2/40 py-16 sm:py-20">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <SectionHeader
          eyebrow="Global delivery"
          title="Global talent. One accountable delivery model."
          copy="Our delivery model combines global talent with structured management and operational oversight."
          align="center"
        />
        <div className="mt-14">
          <DeliveryDiagram />
        </div>
      </div>
    </section>
  );
}

function IndustriesSection() {
  return (
    <section data-testid="industries-section" className="py-16 sm:py-20">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <SectionHeader eyebrow="Industries" title="Built for businesses that need to move faster." />
        <div className="mt-14 grid gap-5 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {INDUSTRIES.map((ind, i) => (
            <Reveal key={ind.name} delay={i * 0.06} className={i === 6 ? "sm:col-span-2 lg:col-span-1" : ""}>
              <div data-testid={`industry-card-${i}`} className="card-hover h-full rounded-2xl border border-white/10 bg-ink-2 p-6">
                <ind.icon className="h-6 w-6 text-brand" aria-hidden="true" />
                <h3 className="mt-4 font-display text-base font-semibold text-white">{ind.name}</h3>
                <p className="mt-2 text-sm leading-relaxed text-mist">{ind.copy}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

function CaseStudiesSection() {
  return (
    <section data-testid="case-studies-section" className="bg-ink-2/40 py-16 sm:py-20">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <SectionHeader eyebrow="Case studies" title="See what managed global teams can do." />
        <div className="mt-14 grid gap-6 md:grid-cols-3">
          {CASE_STUDIES.map((cs, i) => (
            <Reveal key={cs.tag} delay={i * 0.1}>
              <div data-testid={`case-study-card-${i}`} className="card-hover flex h-full flex-col rounded-2xl border border-white/10 bg-ink-2 p-7">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="rounded-full bg-brand-soft px-3 py-1 font-mono text-[10px] font-semibold uppercase tracking-[0.15em] text-brand">
                    {cs.tag}
                  </span>
                  <span className="rounded-full border border-white/10 px-3 py-1 font-mono text-[10px] uppercase tracking-[0.15em] text-slate-500">
                    {cs.location}
                  </span>
                </div>
                <h3 className="mt-4 font-display text-lg font-semibold leading-snug text-white">{cs.title}</h3>
                <div className="mt-5 grid grid-cols-2 gap-3">
                  {cs.stats.map((s) => (
                    <div key={s.label} className="rounded-xl border border-white/10 bg-ink p-4">
                      <p className="font-display text-2xl font-extrabold text-brand">{s.value}</p>
                      <p className="mt-1 text-xs leading-snug text-mist">{s.label}</p>
                    </div>
                  ))}
                </div>
                <Link
                  to="/case-studies"
                  data-testid={`case-study-link-${i}`}
                  className="group mt-auto inline-flex items-center gap-1.5 pt-6 text-sm font-semibold text-brand transition-colors hover:text-brand-deep"
                >
                  Read Case Study
                  <ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                </Link>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

function Pricing() {
  return (
    <section data-testid="pricing-section" className="py-16 sm:py-20">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <SectionHeader
          eyebrow="Pricing"
          title="Flexible teams. Predictable costs."
          copy="Every business has different requirements. Build a team around the roles and capacity you actually need."
        />
        <div className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {PRICING.map((p, i) => (
            <Reveal key={p.name} delay={i * 0.08}>
              <div data-testid={`pricing-card-${i}`} className="card-hover flex h-full flex-col rounded-2xl border border-white/10 bg-ink-2 p-7">
                <h3 className="font-display text-base font-semibold text-white">{p.name}</h3>
                <p className="mt-4">
                  <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">Starting from</span>
                </p>
                <p className="mt-1 font-display text-4xl font-extrabold text-white">
                  {p.rate}
                  {p.unit && <span className="text-base font-semibold text-mist">{p.unit}</span>}
                </p>
                <p className="mt-2 text-xs leading-relaxed text-slate-500">{p.note}</p>
                <ul className="mt-5 flex-1 space-y-2">
                  {p.features.map((f) => (
                    <li key={f} className="flex items-start gap-2 text-sm text-slate-300">
                      <Check className="mt-0.5 h-4 w-4 shrink-0 text-brand" aria-hidden="true" />
                      {f}
                    </li>
                  ))}
                </ul>
              </div>
            </Reveal>
          ))}
        </div>
        <Reveal delay={0.2} className="mt-10 flex flex-col items-center gap-4 text-center">
          <p className="max-w-xl text-sm text-mist">
            Need a specific role? We'll build a tailored proposal around your requirements.
          </p>
          <Link
            to="/get-your-team"
            data-testid="pricing-cta"
            className="group inline-flex items-center gap-2 rounded-full bg-brand px-6 py-3 text-sm font-semibold text-ink transition-colors hover:bg-brand-bright"
          >
            Get a Team Proposal
            <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
          </Link>
        </Reveal>
      </div>
    </section>
  );
}

function FAQSection() {
  return (
    <section data-testid="faq-section" className="bg-ink-2/40 py-16 sm:py-20">
      <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
        <SectionHeader eyebrow="FAQ" title="Frequently asked questions" align="center" />
        <div className="mt-12">
          <Reveal>
            <FAQ items={FAQS} />
          </Reveal>
        </div>
      </div>
    </section>
  );
}

export default function Home() {
  useSEO(
    "Synergy Outsourcing | Managed Global Talent for Australian Businesses",
    "Build dedicated teams across business operations, customer support, sales, finance, IT and professional services with Synergy Outsourcing. An Australian company operating across Australia, the US and Canada."
  );
  return (
    <>
      <Hero />
      <SkillMarquee />
      <Problem />
      <Services />
      <ProServicesFeature />
      <WhySynergy />
      <HowItWorks />
      <GlobalDelivery />
      <IndustriesSection />
      <CaseStudiesSection />
      <Pricing />
      <FAQSection />
      <CTASection />
    </>
  );
}
